<template>
  <BaseDialog
    :title="$t('common.startSelfFeedback')"
    :is-open="isOpen"
    type="responsive"
    :align-dismiss-button-right="true"
    @close="emit('close-dialog')"
  >
    <LoadingWrapper :state="loadingStatus">
      <div v-if="step == 'select-templates'">
        <FeedbackTopicList
          v-if="templates"
          v-model="selfFeedbackFormData.templateIds"
          :bold="true"
          :templates="templates"
          :show-perspectives="false"
          :error="errors.get('templateIds')"
        />
      </div>
      <div v-if="step == 'select-question-groups'">
        <QuestionGroupSectionSelect
          v-model="selfFeedbackFormData.questionGroupIds"
          :templates="configurableTemplates"
          :error="errors.get('questionGroupIds')"
        />
      </div>
      <div v-if="step == 'add-metadata'">
        <BaseInputField
          v-model="selfFeedbackFormData.title"
          type="text"
          :label="$t('common.title')"
          class="bg-surface-container-high"
        />
      </div>
    </LoadingWrapper>
    <template #confirm>
      <div class="flex flex-row gap-1">
        <BaseTextButton v-if="!isFirstStep" size="M" color="outlined" @click="previousStep">{{
          $t('common.back')
        }}</BaseTextButton>
        <BaseTextButton v-if="!isLastStep" size="M" color="filled" @click="nextStep">{{
          $t('common.continue')
        }}</BaseTextButton>
        <BaseTextButton v-if="isLastStep" size="M" color="filled" @click="startSelfFeedback">{{
          $t('common.startSelfFeedback')
        }}</BaseTextButton>
      </div>
    </template>
    <template #dismiss>
      <BaseTextButton color="textOnSurface" size="M" @click="emit('close-dialog')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import FeedbackTopicList from '@/core/components/feedback-topic-list/FeedbackTopicList.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import QuestionGroupSectionSelect from '@/core/components/question-group-section-select/QuestionGroupSectionSelect.vue'
  import { watch } from 'vue'
  import { useStartSelfFeedback } from './use-start-self-feedback'

  interface Props {
    isOpen: boolean
  }

  const { isOpen } = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'open-dialog'): void
    (e: 'close-dialog'): void
  }>()

  const {
    templates,
    errors,
    clearForm,
    selfFeedbackFormData,
    startSelfFeedback,
    step,
    nextStep,
    previousStep,
    isFirstStep,
    isLastStep,
    loadingStatus,
    configurableTemplates,
    autoSelectSingleTemplate,
  } = useStartSelfFeedback(() => emit('close-dialog'))

  watch(
    () => isOpen,
    (newValue) => {
      if (!newValue) {
        clearForm()
      } else {
        autoSelectSingleTemplate()
      }
    },
  )
</script>
